import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useRef } from 'react';
import axios from '../utils/axios';
import { isValidToken, setSession, notifyFlutterLogout } from '../utils/jwt';
import { clearAccessProviderCache } from '../hooks/useAccessProvider';
import { clearCaptivePortalProviderCache } from '../hooks/useCaptivePortalProvider';
import { resolveActiveCampus, persistActiveCampus } from '../utils/activeCampus';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  UPDATE_USER: (state, action) => {
    const updatedUser = action.payload.user;
    return {
      ...state,
      user: updatedUser,
    };
  },
  FETCH_USER_START: (state) => ({
    ...state,
    isLoading: true,
    error: null,
  }),
  FETCH_USER_SUCCESS: (state, action) => ({
    ...state,
    isLoading: false,
    user: action.payload.user,
  }),
  FETCH_USER_ERROR: (state, action) => ({
    ...state,
    isLoading: false,
    error: action.payload.error,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  updateUser: () => Promise.resolve(),
  fetchUser: () => Promise.resolve(),
  hasPermission: () => false,
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error, token = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue.current = [];
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {

      
      const response = await axios.post('/api/v1/accounts/refresh_token/', {
        refresh: refreshToken
      });
      
      const { access, refresh: newRefreshToken } = response.data;
      
      // Store new access token
      localStorage.setItem('accessToken', access);
      setSession(access);
      
      // IMPORTANT: If backend rotates refresh tokens, store the new one
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      
      return access;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      console.error('❌ Error response:', error.response?.data);

      // Only log out when the refresh token was definitively rejected.
      // Network errors / 5xx are transient — keep the session so a flaky
      // connection doesn't force a re-login.
      const status = error?.response?.status ?? error?.status;
      const isDefinitive = status === 400 || status === 401 || status === 403;
      if (isDefinitive) {
        notifyFlutterLogout();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('lastLoginTime');
        localStorage.removeItem('lastBannerFetchTime');

        dispatch({ type: 'LOGOUT' });
      }
      throw error;
    }
  };

  // Settle the active campus the moment the user payload lands, before any
  // route renders. utils/axios injects it as campus_id on every request, and a
  // page's own fetch effect runs BEFORE the layout effect that used to set it,
  // so leaving it to the layout meant the first request of a session could go
  // out against a stale campus - which is how a member ended up asking a campus
  // they hold nothing at whether they could check in.
  const syncActiveCampus = (user) => {
    persistActiveCampus(resolveActiveCampus(user));
  };

  const applyWhitelabelTitle = () => {
    try {
      const cached = localStorage.getItem('campusInfo');
      if (cached) {
        const info = JSON.parse(cached);
        document.title = info.tab_title || info.name || 'DeskOS';
      }
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await axios.get('/api/v1/accounts/user/');
          const { user } = response.data;
          localStorage.setItem('user', JSON.stringify(user));
          syncActiveCampus(user);

          applyWhitelabelTitle();

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else if (refreshToken) {
          // Access token expired but we have refresh token
          // The axios interceptor will handle the refresh automatically
          try {
            const response = await axios.get('/api/v1/accounts/user/');
            const { user } = response.data;
            localStorage.setItem('user', JSON.stringify(user));
            syncActiveCampus(user);

  
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user,
              },
            });
          } catch (err) {
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/api/v1/accounts/login/', {
      email,
      password,
      // Email is unique per organization — the backend scopes login to the
      // org resolved from this domain.
      fullDomain: window.location.hostname,
    });
    
    const { accessToken, refresh, user } = response.data;

    // Store BOTH tokens
    setSession(accessToken);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('lastLoginTime', new Date().toISOString());
    localStorage.setItem('colours', JSON.stringify(user.colours || {}));
    localStorage.setItem('user', JSON.stringify(user));
    syncActiveCampus(user);


    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    return user;
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    
    const { accessToken, refresh, user } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refresh);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    // Must run BEFORE clearing tokens — Flutter needs them to authenticate
    // the FCM device unregister call.
    notifyFlutterLogout();
    setSession(null);
    clearAccessProviderCache();
    clearCaptivePortalProviderCache();
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('lastLoginTime');
    localStorage.removeItem('lastBannerFetchTime');
    localStorage.removeItem('user');
    // The next account to sign in on this device resolves its own campus; a
    // leftover one here would be injected as campus_id on their first requests.
    localStorage.removeItem('activeCampus');
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (updatedUser) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: {
        user: updatedUser,
      },
    });
  };

  /**
   * Check if the current org user has a specific permission code.
   * Reads from user.permissions[] which is populated by the backend on login.
   * Returns false for non-org users.
   */
  const hasPermission = (code) => {
    const { user } = state;
    if (!user || user.role !== 'org') return false;
    const perms = user.permissions;
    // Older cached user objects may predate the permissions field. Fail open
    // for legacy admins only (backend still enforces); everyone else re-syncs
    // on the next app load, which always fetches a fresh user.
    if (!Array.isArray(perms)) return user.orgRole === 'admin';
    return perms.includes(code);
  };

  const fetchUser = async () => {
    dispatch({ type: 'FETCH_USER_START' });
    
    try {
      const response = await axios.get('/api/v1/accounts/user/');
      const { user } = response.data;

        
      dispatch({
        type: 'FETCH_USER_SUCCESS',
        payload: { user },
      });
      
      return user;
    } catch (err) {
      const error = err.message || 'Failed to fetch user data';
      dispatch({
        type: 'FETCH_USER_ERROR',
        payload: { error },
      });
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        updateUser,
        fetchUser,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };